import type { ChargePointUser, PasswordChange, Role } from '../common/redux/api/web-api'

// Transitional replacement for the old localStorage-backed current user helper during auth migration cleanup.
let inMemoryUser: CurrentUser | null | undefined

const emptyCurrentUser: CurrentUser = {
    id: '',
    firstName: '',
    lastName: '',
    password: '',
    passwordChange: null,
    mobile: '',
    email: '',
    userType: '',
    partnerId: 0,
    personalAccept: '',
    companyAccept: null,
    lastLogin: '',
    userName: '',
    fullName: '',
    claims: [],
    isPartner: false,
    userStatus: 'Valid',
    roles: [],
    isSuperAdmin: false,
    roleInCompany: null,
    selectedCompanyId: '',
    features: [],
}

const normalizePasswordChange = (
    passwordChange: Partial<CurrentUserPasswordChange> | PasswordChange | null | undefined
): CurrentUserPasswordChange | null =>
    passwordChange
        ? {
              currentPassword: passwordChange.currentPassword ?? '',
              newPassword: passwordChange.newPassword ?? '',
              passwordCompare:
                  'passwordCompare' in passwordChange && typeof passwordChange.passwordCompare !== 'undefined'
                      ? passwordChange.passwordCompare ?? ''
                      : '',
          }
        : null

const getSelectedCompanyId = (
    user: Partial<CurrentUser> | Partial<ChargePointUser>,
    selectedCompanyId?: string | null
): string => selectedCompanyId ?? ('selectedCompanyId' in user ? user.selectedCompanyId ?? '' : '')

export const normalizeCurrentUser = (
    user?: Partial<CurrentUser> | Partial<ChargePointUser> | null,
    selectedCompanyId?: string | null
): CurrentUser | null => {
    if (!user) return null

    return {
        ...emptyCurrentUser,
        ...user,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        password: user.password ?? '',
        passwordChange: normalizePasswordChange(user.passwordChange),
        mobile: user.mobile ?? '',
        email: user.email ?? '',
        userType: user.userType ?? '',
        partnerId: user.partnerId ?? 0,
        personalAccept: user.personalAccept ?? '',
        companyAccept: user.companyAccept ?? null,
        lastLogin: user.lastLogin ?? '',
        userName: user.userName ?? '',
        fullName: user.fullName ?? '',
        claims: user.claims ?? [],
        isPartner: user.isPartner ?? false,
        userStatus: user.userStatus ?? 'Valid',
        roles: user.roles ?? [],
        isSuperAdmin: user.isSuperAdmin ?? false,
        roleInCompany: user.roleInCompany ?? null,
        selectedCompanyId: getSelectedCompanyId(user, selectedCompanyId),
        features: user.features ?? [],
    }
}

const currentUser = (user?: CurrentUser | null): CurrentUser | null => {
    if (user === null) {
        inMemoryUser = null
        return null
    }

    if (typeof user !== 'undefined') {
        inMemoryUser = user
        return user
    }

    return inMemoryUser ?? null
}

export default currentUser

if (process.env.NODE_ENV === 'development') {
    Object.assign(window, { currentUser })
}

type CurrentUserPasswordChange = PasswordChange & {
    passwordCompare?: string | null
}

export type CurrentUser = {
    id: string
    firstName: string
    lastName: string
    password: string
    passwordChange: CurrentUserPasswordChange | null
    mobile: string
    email: string
    userType: string
    partnerId?: number
    personalAccept: string
    companyAccept: string | null
    lastLogin: string
    userName: string
    fullName: string
    claims: Array<string>
    isPartner: boolean
    userStatus: 'Valid' | 'Locked' | 'New'
    roles: Array<Role>
    isSuperAdmin: boolean
    roleInCompany: string | null
    selectedCompanyId: string
    features: Array<string>
}
